import React, { ChangeEvent, FC, useContext, useEffect, useRef, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import CloseIcon from '@mui/icons-material/Close'
import {
  IconButton,
  Dialog,
  Tooltip,
  useTheme,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  DialogActions,
  Stack,
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
// import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FormProvider, Resolver, SubmitHandler, useForm, useFormState } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import iconTrash from '../../assets/icons/iconTrash.svg'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { UserContext } from '../../user'
import { CustomButton } from '../CustomButton'
import { FormTextField } from '../FormTextField'
import { showToast } from '../Toasts'

import { closeContactUsModal } from './ContactUsModalSlice'
import { ContactUsInput, ContactUsSchema } from './ContactUsSchema'
import { ContactUsService } from './ContactUsService'
import { RelatedFeatureAutomplete } from './RelatedFeatureAutomplete'
import { Success } from './Success'

export const ContactUsModal: FC = () => {
  const { t } = useTranslation()
  const { palette, spacing, breakpoints } = useTheme()

  const fullScreen = useMediaQuery(breakpoints.down('sm'))

  const { currentUser } = useContext(UserContext)
  const { isOpen } = useAppSelector(({ contactUsModal }) => contactUsModal)
  const dispatch = useAppDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [isFormSent, setIsFormSent] = useState<boolean>(false)
  const [isFileToBig, setIsFileToBig] = useState<boolean>(false)

  // const { executeRecaptcha } = useGoogleReCaptcha()

  const methods = useForm<ContactUsInput>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(ContactUsSchema) as unknown as Resolver<ContactUsInput>,
    context: { isAuthorized: !!currentUser },
  })

  const { setValue, trigger, watch, formState, clearErrors, reset, handleSubmit } = methods
  const { isValid } = formState
  const { errors } = useFormState({ control: methods.control })

  const file = watch('file') as File

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsFileToBig(false)
    if (event.target.files) {
      const file = event.target.files[0]
      if (file?.size <= 10 * 1024 * 1024) {
        setValue('file', file)
      } else {
        setIsFileToBig(true)
      }
      trigger('file')
    }
  }

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const deleteFile = () => {
    setValue('file', undefined)
    clearErrors('file')
    trigger()
  }

  const onClose = () => {
    dispatch(closeContactUsModal())
  }

  const onSubmit: SubmitHandler<ContactUsInput> = async (formData) => {
    try {
      setIsLoading(true)
      // if (!executeRecaptcha) {
      //   showToast.error('An error occurred while submitting the form. Please try again later.')
      //   return
      // }
      // const token = await executeRecaptcha('contact_form_submit')

      const data = new FormData()
      data.append('email', currentUser?.email || formData.email?.toLowerCase() || '')
      data.append('message', formData?.message?.trim() || '')
      data.append('relatedFeature', formData?.relatedFeature?.trim() || '')
      if (formData.file) {
        data.append('file', formData.file)
      }

      const { success } = await ContactUsService.sendFormData(data)
      success && setIsFormSent(true)
    } catch (error) {
      onClose()
      error && error.message && showToast.error(error.message)
    } finally {
      reset()
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      !currentUser && setValue('email', '')
      setValue('message', '')
      setValue('relatedFeature', '')
      setValue('file', undefined)
      clearErrors(['email', 'message', 'relatedFeature', 'file'])
      setIsFormSent(false)
      setIsFileToBig(false)
    }
  }, [isOpen, currentUser])

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={fullScreen}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>{t('contactUs.title')}</DialogTitle>
      <IconButton className="close-button" aria-label="close" onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ width: fullScreen ? 'auto' : spacing(60) }}>
        {!isFormSent ? (
          <FormProvider {...methods}>
            <Stack spacing={2} mt={2}>
              {!currentUser && (
                <FormTextField
                  id="modal-email"
                  label={t('contactUs.form.labels.email')}
                  name="email"
                  variant="outlined"
                  fullWidth
                />
              )}
              <FormTextField
                required
                label={t('contactUs.form.labels.message')}
                name="message"
                multiline
                variant="outlined"
                rows={4}
                fullWidth
                {...(!errors.message && {
                  helperText: t('contactUs.form.helperText.message'),
                })}
              />
              <RelatedFeatureAutomplete
                fieldName="relatedFeature"
                variant="outlined"
                label={t('contactUs.form.labels.relatedFeature')}
              />
            </Stack>
            <Box mt={6}>
              <input
                accept="*/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
              />
              <Box display="flex" alignItems="center" gap={2}>
                {isFileToBig && (
                  <Typography color={palette.error.main}>
                    {t('contactUs.form.error.maximumSizeOfTheFileIs10Mb')}
                  </Typography>
                )}
                <Tooltip
                  title={<Typography>{file?.name}</Typography>}
                  placement="top"
                  disableHoverListener={file?.name.length < 33}
                >
                  <Typography noWrap fontWeight={500} maxWidth={spacing(34)}>
                    {file?.name}
                  </Typography>
                </Tooltip>
                {file && (
                  <IconButton onClick={deleteFile}>
                    <img src={iconTrash} alt="trash" />
                  </IconButton>
                )}
              </Box>
              {errors.file && (
                <Typography color="red" mt={1}>
                  {errors.file.message}
                </Typography>
              )}
            </Box>
            <Box mt={5}></Box>
          </FormProvider>
        ) : (
          <Success />
        )}
      </DialogContent>

      {!isFormSent && (
        <DialogActions>
          <CustomButton
            color="secondary"
            width={spacing(17)}
            onClick={handleButtonClick}
            disabled={isLoading}
            sx={{ width: 'fit-content' }}
          >
            {t('contactUs.form.action.addFile')}
          </CustomButton>
          <CustomButton type="submit" width={spacing(17)} disabled={!isValid || isLoading}>
            {t('contactUs.form.action.submit')}
          </CustomButton>
        </DialogActions>
      )}
    </Dialog>
  )
}
